import React, { useState } from 'react';
import { Globe, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { publishAssessment } from '../../services/reviewWorkflowApi';

/**
 * Publish confirmation modal for an assessment (FECOA — publish flow).
 *
 * Shared by every place a publish can be triggered — the card's
 * ReviewWorkflowActions, the Overview Hub "Ready to publish" queue, and the
 * Manage modal — so publishing always goes through one confirm + one API call.
 *
 * Publishing creates a locked version snapshot and flips the assessment live,
 * so we capture a change summary and allow an optional version override
 * (matching the backend publish action). The backend re-validates, so a stale
 * trigger surfaces as a toast rather than a broken state.
 *
 * Props:
 *   assessment  - the assessment object (needs uuid|id + name)
 *   onClose     - close the modal without publishing
 *   onPublished - called with the API result after a successful publish
 */
const PublishAssessmentModal = ({ assessment, onClose, onPublished }) => {
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);

  const uuid = assessment?.uuid || assessment?.id;
  const name = assessment?.name || assessment?.survey?.name || 'this assessment';

  const errorMessage = (err) => {
    const data = err?.response?.data;
    if (Array.isArray(data?.errors) && data.errors.length) {
      return `${data.detail || data.error || 'Could not publish.'} ${data.errors.join(' ')}`;
    }
    return data?.detail || data?.error || 'Could not publish this assessment.';
  };

  const confirm = async () => {
    if (!uuid) return;
    setBusy(true);
    try {
      const result = await publishAssessment(uuid, {
        change_summary: summary.trim(),
      });
      toast.success('Assessment published.');
      onPublished && onPublished(result);
      onClose && onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 text-left whitespace-normal dark:bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose && onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Publish Assessment
            </h3>
            <p className="mt-1 break-words text-[13px] text-gray-500 dark:text-gray-400">{name}</p>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="flex items-start gap-2.5 rounded-lg border border-gray-200/70 bg-gray-50 p-3.5 text-[12.5px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <span>
              Publishing locks a new version snapshot and makes this assessment{' '}
              <strong className="font-medium text-gray-800 dark:text-gray-100">available for use in studies</strong>. Once published, only an
              administrator can revert it to a draft.
            </span>
          </div>
          <div>
            <label className="label">
              Change summary <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. Final copy edits to screens 2–4; ready for study use."
              className="input"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            disabled={busy}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="btn btn-primary"
          >
            <Globe className="h-4 w-4" aria-hidden="true" />
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishAssessmentModal;
